import FormItemContainer from "@/components/FormHeader/FormItemContainer";
import RootModal, { RootModalProps } from "@/components/RootModal/RootModal";
import SwitchWithLabel from "@/components/SwitchWithLabel/SwitchWithLabel";
import useTranslation from "@/hooks/useTranslation";
import { Flex, Form, Input, InputNumber, message } from "antd";
import { useForm, useWatch } from "antd/es/form/Form";
import { ReactNode, useEffect, useState } from "react";
import { useGetProduct, usePatchProduct } from "../../useProducts";
import styles from "./EditProductAction.module.scss";
import PackagingSelector from "./PackagingSelector";

export interface EditProductModalProps extends RootModalProps {
  productId: number;
  customRenderer?: (props: { onClick: () => void }) => ReactNode;
}

export interface EditProductForm {
  id: string;
  name: string;
  mass: number | null;
  category: string | null;
  has_packaging: boolean;
  packaging_ids: number[];
}

export default function EditProductModal({
  productId,
  children,
  customRenderer,
  ...props
}: EditProductModalProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [form] = useForm<EditProductForm>();
  const t = useTranslation();
  const hasPackaging = useWatch("has_packaging", form);
  const packagingIds = useWatch("packaging_ids", form);

  const { data } = useGetProduct({
    productId,
  });

  const patch = usePatchProduct();

  useEffect(() => {
    form.setFieldsValue({
      id: data?.product.external_id,
      name: data?.product.name,
      mass: data?.product.mass,
      category: data?.product.category,
      has_packaging: data?.product.has_packaging,
      packaging_ids: data?.product.packaging_ids,
    });
  }, [data]);

  const onSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        const clear: ("mass" | "category_id")[] = [];
        if (values.mass === null) {
          clear.push("mass");
        }
        if (!values.category) {
          clear.push("category_id");
        }
        patch
          .mutateAsync({
            productId: productId,
            patch: {
              external_id: values.id,
              name: values.name,
              mass: values.mass,
              category: !values.category ? null : values.category,
              has_packaging: values.has_packaging,
              packaging_ids: values.packaging_ids,
              clear,
            },
          })
          .then(() => {
            setIsOpen(false);
            message.success("Product info updated successfully.");
          })
          .catch(() => message.error(t.errors.general));
      })
      .catch(() => message.error("Please fill in all required fields."));
  };

  const removePackagingIds = () => {
    form.setFieldValue("packaging_ids", []);
  };

  return (
    <>
      {customRenderer ? (
        customRenderer({ onClick: () => setIsOpen(true) })
      ) : (
        <span onClick={() => setIsOpen(true)}>{children}</span>
      )}
      <RootModal
        {...props}
        title="Edit product info"
        width={600}
        open={isOpen}
        onOk={onSubmit}
        okText="Save"
        okButtonProps={{ loading: patch.isLoading }}
        onCancel={() => setIsOpen(false)}
      >
        <Form
          form={form}
          onKeyUp={(e) => {
            if (e.code === "Enter") onSubmit();
          }}
        >
          <FormItemContainer
            title="ID"
            isRequired
          >
            <Form.Item name="id">
              <Input
                value={data?.product.external_id}
                disabled={true}
              />
            </Form.Item>
          </FormItemContainer>
          <FormItemContainer
            title="Name"
            isOptional
          >
            <Form.Item name="name">
              <Input />
            </Form.Item>
          </FormItemContainer>
          <FormItemContainer
            title="Category"
            isOptional
          >
            <Form.Item name="category">
              <Input />
            </Form.Item>
          </FormItemContainer>
          <FormItemContainer
            title="Mass"
            isIncomplete={data?.product.mass_status === "incomplete"}
            extra={
              "If no BOM is uploaded, adding a mass is required to distribute facility and transport impact."
            }
          >
            <Form.Item name="mass">
              <InputNumber addonAfter="kg" />
            </Form.Item>
          </FormItemContainer>
          <FormItemContainer
            title="Packaging"
            isIncomplete={data?.product.packaging_status === "incomplete"}
            extra={"Add packaging and connect them to products."}
          >
            <Flex
              gap={8}
              vertical
            >
              <Flex justify="space-between">
                <Form.Item
                  name="has_packaging"
                  noStyle
                  valuePropName="checked"
                >
                  <SwitchWithLabel size="small">Enabled</SwitchWithLabel>
                </Form.Item>
                {packagingIds && packagingIds.length > 0 && (
                  <span
                    onClick={removePackagingIds}
                    className={styles.delete}
                  >
                    Remove all
                  </span>
                )}
              </Flex>
              {hasPackaging && (
                <Form.Item
                  name="packaging_ids"
                  noStyle
                >
                  <PackagingSelector />
                </Form.Item>
              )}
            </Flex>
          </FormItemContainer>
        </Form>
      </RootModal>
    </>
  );
}
